import { minutesToMilliseconds } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';

import { Button } from '@growtherapy/sprout-ui';

import {
  ClientInformationDrawerTab as SeedlingClientInformationDrawerTab,
  useSetActiveClientInfoDrawerTab,
} from '@grow-therapy-team/seedling-components';

import { logger } from '../../datadog/logger';
import { useEndOfSessionToast } from '../../hooks/useEndOfSessionToast';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { toast } from '../../toasts';
import { roomAtom } from '../../video-sdk';
import { useOpenClientInformationCallback } from '../client-information/useClientInformationDrawerCallbacks';
import { hasRecordingAtom } from '../session-recording/state';
import { useSessionSummaryNudgeCount } from '../useSessionSummaryNudgeCount';
import { PromptFollowUpToast } from './PromptFollowUpToast';

const NUDGE_PRIOR_END_TIME_MS = minutesToMilliseconds(5);

export function usePromptFollowUpToast(
  providerShortId: string | undefined,
  inSessionPatientInformation?: {
    patientShortId: string;
    visitorUuid?: string;
  },
) {
  const getIsInSession = useAtomCallback(useCallback((get) => !!get(roomAtom), []));
  const { patientShortId, visitorUuid } = inSessionPatientInformation ?? {};
  const setSeedlingActiveTab = useSetActiveClientInfoDrawerTab();
  const hasRecording = useAtomValue(hasRecordingAtom);
  const { userClicked } = useTrackEvent();

  const openClientInformation = useOpenClientInformationCallback();

  const { increment: incrementSessionSummaryNudgeCount, countBelowMax: showSessionSummaryNudge } =
    useSessionSummaryNudgeCount();

  const openAppointments = useCallback(() => {
    if (!patientShortId) {
      logger.error('Missing patient short ID when opening client information drawer (usePromptFollowUpToast)');
      return;
    }
    openClientInformation({ patientShortId, visitorUuid });
    setSeedlingActiveTab(SeedlingClientInformationDrawerTab.APPOINTMENTS);
  }, [openClientInformation, patientShortId, setSeedlingActiveTab, visitorUuid]);

  const showToast = useCallback(() => {
    toast.enqueue(
      ({ closeToast, ...ariaProps }) => {
        const title = hasRecording && showSessionSummaryNudge ? 'Documentation Tip' : '5 Minute Reminder';
        const subtitle =
          hasRecording && showSessionSummaryNudge ? (
            `Click "Complete Session" to process the AI summary immediately after the session.`
          ) : (
            // TODO(CARED-1599): toast should handle if a follow up is already scheduled
            <>
              Consider wrapping up your discussion, and setting up your next appointment from{' '}
              <Button
                onClick={() => {
                  closeToast();
                  openAppointments();
                  userClicked(EventTypeEnum.FIVE_MINUTE_REMINDER_TOAST_SCHEDULING_PROMPT, {
                    patientShortId: patientShortId ?? '',
                    providerShortId: providerShortId ?? '',
                  });
                }}
                use='unstyled'
                className='underline decoration-neutral-600 inline p-0'
              >
                the appointments tab
              </Button>
              .
            </>
          );

        return <PromptFollowUpToast {...ariaProps} title={title} subtitle={subtitle} />;
      },
      {
        onClose: hasRecording && showSessionSummaryNudge ? incrementSessionSummaryNudgeCount : undefined,
        purpose: 'prompt',
        timeout: minutesToMilliseconds(1),
      },
    );
  }, [
    hasRecording,
    incrementSessionSummaryNudgeCount,
    openAppointments,
    showSessionSummaryNudge,
    patientShortId,
    providerShortId,
    userClicked,
  ]);

  useEndOfSessionToast(providerShortId, patientShortId, showToast, NUDGE_PRIOR_END_TIME_MS, getIsInSession());
}
